import { applyDatabinding, evaluateCondition } from '@dynamic/ui';
import { Dispatch, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router';

import {
  CollectionData,
  ContainerData,
  FeedResponse,
  LayoutData,
  PageLayout,
  usePageLayout,
  V2TemplatesData,
} from '#/client';
import {
  cancelOldRequests,
  DEFAULT_PAGE_SIZE,
  fetchFeed,
} from '#/dynamic/dynui-fetchers';
import { useConfig, useSeoInfo } from '#/hooks';
import { handleScreenView } from '#/plugins/analytics/analyticsUtils';
import { FormFactors } from '#/service/constants';
import { Path } from '#/service/routes';
import { useAuthentication } from '#/store/auth';
import { useTranslations } from '#/store/translation/translation.store';

import { PageControllerProps, PageParams } from './pageTypes';

const MULTIVIEW_TABS_PAGE_SIZE = 100; // needed for initial container fetch. 100 is the max page size permitted by Core.

const useLayout = (
  hardcodedLayout: PageControllerProps['hardcodedLayout'],
  setV2Templates: Dispatch<any>
) => {
  const [layout, setLayout] = useState<LayoutData>();
  // hardcoded layout?
  // TODO: Csaba's temporary fix to make hardcoded v2 work. make this better.
  useEffect(() => {
    if (!hardcodedLayout || hardcodedLayout === layout) {
      return;
    }

    const v2Layout = hardcodedLayout as PageLayout;

    if (v2Layout.page && v2Layout.templates) {
      // v2
      const { page: pageLayout, templates: pageTemplates } = v2Layout;
      const { pages, containers: pageContainers, items } = pageTemplates || {};

      if (pageLayout !== layout) {
        setV2Templates({ ...pages, ...pageContainers, ...items });
        setLayout(pageLayout);
      }
    } else {
      // v1
      setLayout(hardcodedLayout as LayoutData);
    }
  }, [hardcodedLayout, layout, setV2Templates]);

  return { layout, setLayout };
};

export function usePageController(
  formFactor: FormFactors,
  {
    defaultContext = {},
    pageId: pageIdProp,
    hardcodedLayout,
    customPageTitle,
    cacheKey,
  }: PageControllerProps
) {
  const { isLoggedIn, isEntitled } = useAuthentication();
  const { pageId: pageIdPageParams } = useParams<PageParams>();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [v2Templates, setV2Templates] = useState<any>();
  const { layout, setLayout } = useLayout(hardcodedLayout, setV2Templates);
  const {
    app: { parentalSystem },
    theme,
    pages,
  } = useConfig();

  const { translations, locale } = useTranslations();

  const pageId = useMemo(() => {
    // id or alias?
    const pageIdOrAlias = pageIdProp ?? pageIdPageParams;

    return (
      (pageIdOrAlias && pages[pageIdOrAlias.toUpperCase()]) || pageIdOrAlias
    );
  }, [pageIdProp, pageIdPageParams, pages]);

  const isPageLoading =
    isLoading ||
    (!hardcodedLayout && !isLayoutAndTemplateMapped(v2Templates, layout));

  const { error, data: pageLayout } = usePageLayout(
    hardcodedLayout ? undefined : pageId
  );

  validatePageLayout(pageLayout, pageId);
  const pageTitle = useSeoInfo({
    title: customPageTitle ?? defaultContext.item?.title,
  });

  useEffect(() => {
    handleScreenView({ item: defaultContext.item });
  }, [defaultContext.item]);

  useEffect(() => {
    setIsLoading(true);

    return cancelRequestIfNeeded(location.pathname);
  }, [location.pathname]);

  const containerMapper = useCallback(
    async (container: ContainerData) => {
      const originalFeed = container.data.originalFeed ?? '';
      const databoundFeed = getFeedEndpoint(originalFeed, defaultContext);
      const formFactorTemplate = container.templates ?? {};
      const isMultiView = isMultiViewContainer(
        formFactorTemplate[formFactor].id,
        pageLayout?.templates
      );

      const isHidden =
        container?.visibility?.type &&
        !evaluateCondition(container.visibility!, defaultContext);

      if (!isMultiView || isHidden) {
        // no initial for fetch sliders and grids,
        // no fetch for MultiViews hidden by visibility condition either,
        // just return an empty response
        return Promise.resolve({
          collection: { id: container.data.collectionId },
          feed: databoundFeed,
          data: [],
          pageInfo: {
            hasMore: true,
            pageSize: DEFAULT_PAGE_SIZE,
          },
        });
      }

      // initial fetch for multiviews
      return fetchFeed(databoundFeed, 1, MULTIVIEW_TABS_PAGE_SIZE).catch(
        (err) => {
          console.error(`Container feed failed`, {
            containerId: container.id,
            error: err.message,
          });

          return null;
        }
      );
    },
    [defaultContext, formFactor, pageLayout]
  );

  const getContainerPromises = useCallback(
    (containers: ContainerData[]): Promise<FeedResponse>[] =>
      containers
        .filter(Boolean) // exclude null/wrong containers
        .map(containerMapper)
        // exclude containers with failed initial fetch
        .filter(Boolean) as Promise<FeedResponse>[],
    [containerMapper]
  );

  useEffect(() => {
    // It's like this because loading makes sense where hardcoded layout was not provided
    setIsLoading(!hardcodedLayout);

    if (!pageLayout) {
      return;
    }

    const collections = pageLayout.page.containerCollections;

    if (!collections) {
      throw new Error(
        `PageController - containerCollections not found in page layout.`
      );
    }

    const containers = getContainers(collections);
    const containerPromises = getContainerPromises(containers);

    Promise.all(containerPromises)
      .then((feedResponses) => {
        // merge container feed data into the layout
        containers.forEach(mapContainerData(feedResponses));
        console.debug('PageController - Page fetched', pageLayout.page);
        setLayout(pageLayout.page);
        const {
          pages,
          containers: pageContainers,
          items,
        } = pageLayout.templates;
        setV2Templates({ ...pages, ...pageContainers, ...items });
      })
      .finally(() => {
        setIsLoading(false);
      });
    // 1. We're not using cacheKey, isLoggedIn and isEntitled, but we need to rerender when they change,
    //    to remove conditional containers, like "continue watching".
    // 2. locale is required to trigger a re-render of the page when user changes locale
  }, [
    cacheKey,
    getContainerPromises,
    hardcodedLayout,
    isEntitled,
    isLoggedIn,
    locale,
    pageLayout,
    setLayout,
  ]);

  return {
    layout,
    isPageLoading,
    error,
    theme,
    translations,
    parentalSystem,
    v2Templates,
    defaultContext,
    pageTitle,
  };
}

const getFeedEndpoint = (feed: string, context: object) =>
  applyDatabinding({
    value: decodeURIComponent(feed),
    context,
  });

const getResourceIdAndType = (feedUrl: string) => {
  const feedUrlArr = feedUrl.split('/');
  const resourceId = feedUrlArr[feedUrlArr.length - 1];
  const resourceType = feedUrlArr[feedUrlArr.length - 2];

  return { resourceId, resourceType };
};

const getContainers = (collections: CollectionData[]) =>
  collections
    .map((collection) => collection.containers)
    .flat()
    .map((container) => {
      // store the original `feed` so we can do databinding on `originalFeed` even after `feed` has been mutated.
      container.data.originalFeed ||= container.data.feed;

      return container;
    });

const mapContainerData =
  (feedResponses: FeedResponse[]) =>
  (container: ContainerData, index: number) => {
    const feedResponse = feedResponses[index];
    const databoundFeed = feedResponse?.feed;

    // mutate the container feed to the URI decoded & databound result from getContainerPromises
    if (databoundFeed) {
      container.data.feed = databoundFeed;
    }
    container.data.initial =
      feedResponse?.data?.length > 0 ? feedResponse.data : undefined;
    container.data.pageInfo = feedResponse?.pageInfo || {};
    const { resourceId } = getResourceIdAndType(container.data.feed);
    container.data.collectionId = resourceId || '';
  };

// multiview container should contain only one children element
const isMultiViewContainer = (
  templateId: string,
  templates?: V2TemplatesData
) => {
  if (!templates) {
    return false;
  }

  const { children: containerConfig } =
    templates?.containers[templateId]?.component ?? {};

  return (
    containerConfig?.length === 1 && containerConfig[0].type === 'MULTI_VIEW'
  );
};

const isLayoutAndTemplateMapped = (v2Templates: any, layout?: LayoutData) => {
  const { templates = {} } = layout ?? {};

  if (!v2Templates) {
    return false;
  }

  return Object.values(templates).some((template) => {
    const { id } = template;

    return id && v2Templates[id];
  });
};

function validatePageLayout(data?: PageLayout, pageId?: string) {
  if (!data) {
    return;
  }

  const pageLayout = data.page;
  const pageTemplates = data.templates;

  if (!pageLayout) {
    throw new Error(`PageController - Page not found: ${pageId}`);
  }

  if (!pageTemplates) {
    throw new Error(`PageController - Page templates not found: ${pageId}`);
  }
}

const cancelRequestIfNeeded = (pathname: string) => {
  // Only cancel requests if we were not on the search page
  // because search page replaces the search query for each input change.
  // In such cases it could happen that it fires the request right before the return
  // of this hook would be called so that search request would be immediately canceled.
  if (pathname !== Path.Search) {
    cancelOldRequests();
  }
};
